// src/components/rails/ServiceRailOmni.tsx
import React, { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getProductNavItemById,
  getServiceRailProductNavItems,
  isProductNavActive,
} from "@/config/productNavigation";
import { uniqueBy } from "@/lib/uniqueBy";
import { safeKey } from "@/lib/reactKey";

import {
  type ServiceId,
  useServiceLive,
  useServiceLiveLabel,
  useServiceTimestamp,
} from "@/state/useServiceStatus";

type ServiceDef = {
  id: string;
  label: string;
  icon: React.ComponentType<any>;
  path: string;
  statusId?: ServiceId;
};

const SERVICES: readonly ServiceDef[] = getServiceRailProductNavItems().map((item) => ({
  id: item.id,
  label: item.label,
  icon: item.icon,
  path: item.path,
  statusId: item.statusId,
}));

type Row = {
  id: string;
  name: string;
  route: string;
  icon: React.ComponentType<any>;
  statusId?: ServiceId;
};

function agoShort(ts?: number) {
  if (!ts) return "";
  const s = Math.max(1, Math.floor((Date.now() - ts) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h`;
}

// ── Updated pill: smaller paddings, lighter glass, subtler hover/active ─────────
const pillClass = (active: boolean) =>
  [
    "group w-full rounded-2xl px-4 py-3 text-left transition-all duration-150",
    "focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40",
    "backdrop-blur-[14px] saturate-[1.15]",
    "bg-[radial-gradient(180%_120%_at_10%_0%,rgba(255,255,255,0.28)_0%,rgba(255,255,255,0.12)_20%,rgba(255,255,255,0.05)_42%,rgba(255,255,255,0.02)_60%,rgba(255,255,255,0)_100%),linear-gradient(180deg,rgba(152,200,255,0.22)_0%,rgba(120,170,240,0.12)_30%,rgba(255,255,255,0.06)_100%)]",
    "border border-[rgba(255,255,255,0.18)]",
    "shadow-[inset_0_1px_0_rgba(255,255,255,0.30),inset_0_-1px_0_rgba(0,0,0,0.16),0_6px_16px_rgba(10,20,40,0.22),0_18px_40px_rgba(10,20,40,0.20)]",
    "hover:-translate-y-[1px] hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.38),inset_0_-1px_0_rgba(0,0,0,0.18),0_10px_22px_rgba(10,20,40,0.26),0_26px_58px_rgba(10,20,40,0.24)]",
    active &&
      "border-[rgba(170,210,255,0.42)] bg-[radial-gradient(180%_120%_at_10%_0%,rgba(255,255,255,0.36)_0%,rgba(255,255,255,0.16)_18%,rgba(255,255,255,0.07)_44%,rgba(255,255,255,0.03)_62%,rgba(255,255,255,0)_100%),linear-gradient(180deg,rgba(170,215,255,0.30)_0%,rgba(130,185,255,0.20)_32%,rgba(255,255,255,0.08)_100%)] shadow-[inset_0_1px_0_rgba(200,230,255,0.48),inset_0_-1px_0_rgba(0,0,0,0.20),0_12px_26px_rgba(14,36,80,0.34),0_32px_90px_rgba(14,36,80,0.28)]",
  ]
    .filter(Boolean)
    .join(" ");

// ── Updated icon tile: 36px square, lighter ring/shadow ─────────────────────────
const iconTileClass =
  "grid h-9 w-9 place-items-center rounded-xl " +
  "backdrop-blur-[9px] " +
  "bg-[linear-gradient(180deg,rgba(255,255,255,0.12),rgba(255,255,255,0.05))] " +
  "border border-[rgba(255,255,255,0.18)] " +
  "shadow-[inset_0_1px_0_rgba(255,255,255,0.28),0_8px_18px_rgba(0,0,0,0.22)] " +
  "text-white/95";

function ServiceRailRow({
  svc,
  index,
  active,
  activeClass,
  onPress,
}: {
  svc: Row;
  index: number;
  active: boolean;
  activeClass: boolean;
  onPress: () => void;
}) {
  // Hooks MUST live here (top-level of a component), not inside parent map()
  const live = svc.statusId ? useServiceLive(svc.statusId) : false;
  const liveLabel = svc.statusId ? useServiceLiveLabel(svc.statusId) : "Idle";
  const ts = svc.statusId ? useServiceTimestamp(svc.statusId) : undefined;

  const subline = live && ts ? `${liveLabel} · ${agoShort(ts)}` : "Press to switch";
  const Icon = svc.icon;

  return (
    <li key={safeKey("svc-li", svc.id, index)}>
      <button
        type="button"
        className={pillClass(activeClass)}
        onClick={onPress}
        aria-pressed={active}
      >
        <div className="flex items-center gap-3">
          <div className={iconTileClass}>
            <Icon />
          </div>

          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="truncate font-semibold text-white/95">{svc.name}</span>

              {active ? (
                <span className="rounded-full bg-emerald-300/18 px-2 py-0.5 text-xs text-emerald-200 ring-1 ring-emerald-300/30">
                  Open
                </span>
              ) : live ? (
                <span className="rounded-full bg-amber-300/18 px-2 py-0.5 text-xs text-amber-200 ring-1 ring-amber-300/30">
                  {liveLabel}
                </span>
              ) : null}
            </div>

            <div className="text-xs text-white/65">{subline}</div>
          </div>
        </div>
      </button>
    </li>
  );
}

export default function ServiceRailOmni({ onNavigate }: { onNavigate?: () => void } = {}) {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const rows: Row[] = useMemo(
    () =>
      uniqueBy(
        SERVICES.map((s) => ({
          id: s.id,
          name: s.label,
          route: s.path,
          icon: s.icon,
          statusId: s.statusId,
        })),
        (r) => r.id || r.route || r.name,
      ),
    [],
  );

  // GoalBuddy context check — include both legacy and app routes
  const isGoalBuddyContext =
    pathname === "/" ||
    pathname.startsWith("/goalbuddy") ||
    pathname.startsWith("/goalbuddy/app") ||
    pathname.startsWith("/goalbuddy/start");

  return (
    <nav
      data-rail="goalbuddy-service-rail"
      aria-label="Services"
      className="relative z-[30] pointer-events-auto sticky"
      style={{
        top: "calc(var(--tabsbar-offset, 72px) - var(--rail-nudge, 16px))",
        transform: "translateX(var(--rail-left-nudge, -12px))",
      }}
    >
      <ul className="space-y-2 pr-1">
        {rows.map((svc, i) => {
          const item = getProductNavItemById(svc.id);
          const active =
            (item ? isProductNavActive(pathname, item) : pathname.startsWith(svc.route)) ||
            (svc.id === "goalbuddy" && pathname === "/");

          return (
            <ServiceRailRow
              key={safeKey("svc-row", svc.id, i)}
              svc={svc}
              index={i}
              active={active}
              activeClass={active && isGoalBuddyContext}
              onPress={() => {
                navigate(svc.route);
                onNavigate?.();
              }}
            />
          );
        })}
      </ul>
    </nav>
  );
}
